import { useEffect, useLayoutEffect, useReducer, useRef, useState } from "react";
import {
  createDefaultScoreConfig,
  DEFAULT_AMENITY_PACK_ID,
} from "../shared/amenityPacks";
import { defaultFilters, DEFAULT_RADIUS_METERS, normalizeSearchFilters } from "../shared/filters";
import { buildResolvedScoreBands } from "../shared/locationScore";
import type {
  AggregateBreakdownResponse,
  AggregateSummaryResponse,
  AmenityPackId,
  PlaceSelection,
  RuntimeConfig,
  ScoreConfig,
  SearchFilters,
} from "../shared/types";
import { fetchBreakdown, fetchSummary, loadRuntimeConfig } from "./lib/api";
import {
  initialMobileSheetState,
  mobileSheetReducer,
} from "./lib/sheetState";
import {
  applyResolvedTheme,
  getSystemTheme,
  THEME_MEDIA_QUERY,
  type ResolvedTheme,
} from "./lib/theme";
import { BottomSheet } from "./components/BottomSheet";
import { FilterPanel } from "./components/FilterPanel";
import { LocationSnapshotPanel } from "./components/LocationSnapshotPanel";
import { MapView } from "./components/MapView";

const DEFAULT_MAP_ID = "bf8bb13269df30a68aca0cab";

function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 960px)").matches
      : false,
  );

  useEffect(() => {
    const media = window.matchMedia("(max-width: 960px)");
    const update = () => setIsMobile(media.matches);

    update();
    media.addEventListener("change", update);

    return () => {
      media.removeEventListener("change", update);
    };
  }, []);

  return isMobile;
}

export default function App() {
  const isMobile = useIsMobile();
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() =>
    typeof window !== "undefined"
      ? getSystemTheme(window.matchMedia(THEME_MEDIA_QUERY))
      : "light",
  );
  const [mobileSheets, dispatchMobileSheets] = useReducer(
    mobileSheetReducer,
    initialMobileSheetState,
  );
  const [config, setConfig] = useState<RuntimeConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState<string>();
  const [draftFilters, setDraftFilters] = useState<SearchFilters>(defaultFilters);
  const [appliedFilters, setAppliedFilters] = useState<SearchFilters>(defaultFilters);
  const [draftPackId, setDraftPackId] = useState<AmenityPackId>(DEFAULT_AMENITY_PACK_ID);
  const [appliedPackId, setAppliedPackId] = useState<AmenityPackId>(DEFAULT_AMENITY_PACK_ID);
  const [draftScoreConfig, setDraftScoreConfig] = useState<ScoreConfig>(
    createDefaultScoreConfig(DEFAULT_AMENITY_PACK_ID),
  );
  const [appliedScoreConfig, setAppliedScoreConfig] = useState<ScoreConfig>(
    createDefaultScoreConfig(DEFAULT_AMENITY_PACK_ID),
  );
  const [draftRadiusMeters, setDraftRadiusMeters] = useState(DEFAULT_RADIUS_METERS);
  const [appliedRadiusMeters, setAppliedRadiusMeters] = useState(DEFAULT_RADIUS_METERS);
  const [searchLocation, setSearchLocation] = useState<PlaceSelection | null>(null);
  const [summary, setSummary] = useState<AggregateSummaryResponse | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState<string>();
  const [breakdown, setBreakdown] = useState<AggregateBreakdownResponse | null>(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [breakdownError, setBreakdownError] = useState<string>();
  const [searchFocusVersion, setSearchFocusVersion] = useState(0);
  const summaryRequestId = useRef(0);
  const breakdownRequestId = useRef(0);
  const appliedScoreBands = buildResolvedScoreBands(
    appliedRadiusMeters,
    appliedScoreConfig,
    appliedPackId,
  );

  useEffect(() => {
    const media = window.matchMedia(THEME_MEDIA_QUERY);
    const update = () => setResolvedTheme(getSystemTheme(media));

    update();
    media.addEventListener("change", update);

    return () => {
      media.removeEventListener("change", update);
    };
  }, []);

  useLayoutEffect(() => {
    applyResolvedTheme(document, resolvedTheme);
  }, [resolvedTheme]);

  useEffect(() => {
    if (!isMobile) {
      dispatchMobileSheets({ type: "reset-desktop" });
    }
  }, [isMobile]);

  useEffect(() => {
    const abortController = new AbortController();

    setConfigLoading(true);
    loadRuntimeConfig(abortController.signal)
      .then((response) => {
        setConfig(response);
        setConfigError(undefined);
      })
      .catch((error) => {
        if (abortController.signal.aborted) {
          return;
        }

        setConfigError(error instanceof Error ? error.message : "Failed to load runtime config.");
      })
      .finally(() => {
        if (!abortController.signal.aborted) {
          setConfigLoading(false);
        }
      });

    return () => {
      abortController.abort();
    };
  }, []);

  async function runSummarySearch(
    location: PlaceSelection,
    filters: SearchFilters,
    radiusMeters: number,
  ) {
    const currentRequestId = summaryRequestId.current + 1;
    summaryRequestId.current = currentRequestId;
    setSummaryLoading(true);
    setSummaryError(undefined);

    try {
      const response = await fetchSummary({
        center: location.center,
        radiusMeters,
        filters,
      });

      if (summaryRequestId.current !== currentRequestId) {
        return;
      }

      setSummary(response);

      if (isMobile) {
        dispatchMobileSheets({
          type: "show-results",
          snap: "half",
        });
      }
    } catch (error) {
      if (summaryRequestId.current !== currentRequestId) {
        return;
      }

      setSummaryError(error instanceof Error ? error.message : "Summary request failed.");
      setSummary(null);
    } finally {
      if (summaryRequestId.current === currentRequestId) {
        setSummaryLoading(false);
      }
    }
  }

  function handlePlaceSelected(place: PlaceSelection) {
    const nextFilters = normalizeSearchFilters(draftFilters);

    setSearchLocation(place);
    setAppliedFilters(nextFilters);
    setAppliedPackId(draftPackId);
    setAppliedScoreConfig(draftScoreConfig);
    setAppliedRadiusMeters(draftRadiusMeters);
    setSummary(null);
    setBreakdown(null);
    setBreakdownError(undefined);
    setSearchFocusVersion((current) => current + 1);
    void runSummarySearch(place, nextFilters, draftRadiusMeters);
  }

  function handleApplyFilters() {
    const nextFilters = normalizeSearchFilters(draftFilters);

    setAppliedFilters(nextFilters);
    setAppliedPackId(draftPackId);
    setAppliedScoreConfig(draftScoreConfig);
    setAppliedRadiusMeters(draftRadiusMeters);
    setBreakdown(null);
    setBreakdownError(undefined);
    setSearchFocusVersion((current) => current + 1);

    if (isMobile) {
      dispatchMobileSheets({ type: "apply-filters" });
    }

    if (searchLocation) {
      void runSummarySearch(searchLocation, nextFilters, draftRadiusMeters);
    }
  }

  useEffect(() => {
    if (!searchLocation) {
      return;
    }

    const abortController = new AbortController();
    const requestId = breakdownRequestId.current + 1;

    breakdownRequestId.current = requestId;
    setBreakdownLoading(true);
    setBreakdownError(undefined);

    void fetchBreakdown(
      {
        center: searchLocation.center,
        radiusMeters: appliedRadiusMeters,
        filters: appliedFilters,
        activePackId: appliedPackId,
        scoreConfig: appliedScoreConfig,
      },
      abortController.signal,
    )
      .then((response) => {
        if (abortController.signal.aborted || breakdownRequestId.current !== requestId) {
          return;
        }

        setBreakdown(response);

        if (isMobile) {
          dispatchMobileSheets({
            type: "show-results",
            snap: "half",
          });
        }
      })
      .catch((error) => {
        if (abortController.signal.aborted || breakdownRequestId.current !== requestId) {
          return;
        }

        setBreakdownError(error instanceof Error ? error.message : "Snapshot request failed.");
        setBreakdown(null);
      })
      .finally(() => {
        if (!abortController.signal.aborted && breakdownRequestId.current === requestId) {
          setBreakdownLoading(false);
        }
      });

    return () => {
      abortController.abort();
    };
  }, [appliedFilters, appliedPackId, appliedRadiusMeters, appliedScoreConfig, isMobile, searchLocation]);

  const showMobileResults =
    summary !== null ||
    Boolean(summaryError) ||
    breakdown !== null ||
    breakdownLoading ||
    Boolean(breakdownError);

  return (
    <div className="app-shell">
      <header className="chrome-header">
        <div className="chrome-header__title-block">
          <p className="eyebrow">Linnaean × Google Maps</p>
          <h1>Places Aggregate API Explorer</h1>
        </div>
        <p className="chrome-header__copy">
          Search an address, then score nearby amenities through a multifamily lens.
        </p>
      </header>

      <main className="workspace">
        {!isMobile ? (
          <aside className="workspace__rail workspace__rail--filters">
            <FilterPanel
              activePackId={draftPackId}
              filters={draftFilters}
              isBusy={summaryLoading}
              onActivePackChange={setDraftPackId}
              onApply={handleApplyFilters}
              onFiltersChange={setDraftFilters}
              onRadiusChange={setDraftRadiusMeters}
              onScoreConfigChange={setDraftScoreConfig}
              radiusMeters={draftRadiusMeters}
              scoreConfig={draftScoreConfig}
            />
          </aside>
        ) : null}

        <section className="workspace__map">
          <MapView
            browserApiKey={config?.browserApiKey ?? ""}
            cells={[]}
            mapId={config?.mapId || DEFAULT_MAP_ID}
            onCellSelect={() => undefined}
            onMapInteraction={() => {
              if (isMobile) {
                dispatchMobileSheets({ type: "map-interaction" });
              }
            }}
            onPlaceSelected={handlePlaceSelected}
            onViewportIdle={() => undefined}
            places={[]}
            radiusMeters={appliedRadiusMeters}
            scoreBands={appliedScoreBands}
            searchCenter={searchLocation}
            searchFocusVersion={searchFocusVersion}
            selectedCellId={undefined}
            showHeatLayer={false}
            showScoreBands
            theme={resolvedTheme}
          />

          <div className="map-overlay map-overlay--left">
            <div className="status-stack">
              {configLoading ? <span className="pill">Loading runtime config…</span> : null}
              {breakdownLoading ? <span className="pill">Refreshing snapshot…</span> : null}
            </div>
          </div>

          {isMobile ? (
            <div className="map-overlay map-overlay--mobile-actions">
              <button
                className="primary-button primary-button--floating"
                onClick={() => dispatchMobileSheets({ type: "open-filters" })}
                type="button"
              >
                Filters
              </button>
            </div>
          ) : null}

          {!configLoading && !config?.browserApiKey ? (
            <div className="setup-card">
              <p className="eyebrow">Setup needed</p>
              <h2>Add a browser-facing Maps API key.</h2>
              <p>
                Configure <code>GOOGLE_MAPS_BROWSER_API_KEY</code> for Pages, plus the server-side
                <code> GOOGLE_PLACES_SERVER_API_KEY</code> secret for Aggregate and Place Details.
              </p>
            </div>
          ) : null}

          {configError ? (
            <div className="setup-card setup-card--error">
              <p className="eyebrow">Runtime config error</p>
              <h2>{configError}</h2>
            </div>
          ) : null}
        </section>

        {!isMobile ? (
          <aside className="workspace__rail workspace__rail--results">
            <LocationSnapshotPanel
              breakdown={breakdown}
              breakdownError={breakdownError}
              breakdownLoading={breakdownLoading}
              filters={appliedFilters}
              location={searchLocation}
              radiusMeters={appliedRadiusMeters}
              summaryCount={summary?.count}
              summaryError={summaryError}
              summaryLoading={summaryLoading}
            />
          </aside>
        ) : null}
      </main>

      {isMobile ? (
        <BottomSheet
          modal
          onClose={() => dispatchMobileSheets({ type: "close-filters" })}
          snap={mobileSheets.filtersOpen ? "full" : "hidden"}
          title="Filters"
        >
          <FilterPanel
            activePackId={draftPackId}
            filters={draftFilters}
            isBusy={summaryLoading}
            onActivePackChange={setDraftPackId}
            onApply={handleApplyFilters}
            onFiltersChange={setDraftFilters}
            onRadiusChange={setDraftRadiusMeters}
            onScoreConfigChange={setDraftScoreConfig}
            radiusMeters={draftRadiusMeters}
            scoreConfig={draftScoreConfig}
          />
        </BottomSheet>
      ) : null}

      {isMobile ? (
        <BottomSheet
          onClose={() => dispatchMobileSheets({ type: "hide-results" })}
          onSnapChange={(snap) => dispatchMobileSheets({ type: "set-results-snap", snap })}
          snap={showMobileResults ? mobileSheets.resultsSnap : "hidden"}
          title="Snapshot"
        >
          <LocationSnapshotPanel
            breakdown={breakdown}
            breakdownError={breakdownError}
            breakdownLoading={breakdownLoading}
            filters={appliedFilters}
            location={searchLocation}
            radiusMeters={appliedRadiusMeters}
            summaryCount={summary?.count}
            summaryError={summaryError}
            summaryLoading={summaryLoading}
          />
        </BottomSheet>
      ) : null}
    </div>
  );
}
